<template>
  <div class="drag-drop-view">
    <cv-form @submit.prevent>
      <!-- Instructions -->
      <dl class="field-set type-text instructions">
        <dt class="field-label">
          <svg-icon name="Info16" kind="indicative" />
          <span>{{ t("question_component_label_instructions") }}</span>
        </dt>
        <dd class="field-value">
          {{ t("question_component_label_dnd_instructions_content") }}
        </dd>
      </dl>

      <!-- Question Body -->
      <match-indicator v-if="highlights" />
      <dl ref="container" class="field-set type-text question-body">
        <dd class="field-value">
          <rich-text-wrapper
            :content="questionBody"
            :expandable="true"
            :expanded="true"
            :can-annotate="canAccessAnnotations"
            :anchor="questionBodyAnchor"
            :resource-type="'LESSON_COMPONENT'"
            :annotation-resource-id="questionAnnotationResourceId"
          />
        </dd>
      </dl>

      <!-- Hint -->
      <dl v-if="questionHints && questionHints.length" class="field-set type-text hint">
        <dd class="field-value">
          <AccordionWrapper v-for="(hint, index) in questionHints" :key="hintId(hint, index)">
            <cv-accordion-item :open="true">
              <template #title>
                <span class="icon">
                  <svg-icon name="Hint16" kind="indicative" />
                </span>
                <span class="title">{{ hintTitle(index) }}</span>
              </template>
              <template #content>
                <rich-text class="hint" :content="hint.description" :expandable="false" />
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </dd>
      </dl>

      <dl class="field-set question-view">
        <dd class="field-value">
          <div class="bg-image-wrapper">
            <div class="bg-image-view">
              <div class="indicator" :class="indicatorClasses" @click.once="handleIndicatorClick">
                <svg-icon name="Pinch32" kind="indicative" />
                <span>{{ t("question_dnd_label_pinch_zoom_indicator") }}</span>
              </div>
              <svg ref="imageWrapper" class="image-wrapper">
                <g>
                  <drag-drop-response-option
                    v-for="correctAnswerOption in correctAnswerOptions"
                    :key="correctAnswerOption.id"
                    :response-option="correctAnswerOption"
                    :question-uuid="question.uuid"
                    :bg-image-ratio="bgImageRatio"
                  />
                </g>
              </svg>
            </div>

            <attachment-placeholder class="image-toolbar">
              <template #zoom-tools>
                <button class="zoom-in">
                  <svg-icon name="ZoomIn24" kind="action" />
                </button>
                <button class="zoom-out">
                  <svg-icon name="ZoomOut24" kind="action" />
                </button>
                <button class="zoom-reset">
                  <svg-icon name="Refresh24" kind="action" />
                </button>
              </template>
            </attachment-placeholder>
          </div>
        </dd>
      </dl>

      <dl class="field-set unused-options">
        <dd class="field-value">
          <AccordionWrapper v-if="questionAnswerUnusedOptions.length > 0" class="unused-options">
            <cv-accordion-item>
              <template #title>
                {{ t("question_component_label_show_unused_options") }}
              </template>
              <template #content>
                <drag-drop-unused-option
                  v-for="unusedOption in questionAnswerUnusedOptions"
                  :key="unusedOption.id"
                  :option="unusedOption"
                />
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </dd>
      </dl>

      <div class="feedback-divider"></div>
      <!-- Feedback(Explanation) -->
      <dl v-if="feedback" class="field-set type-text feedback">
        <dd class="field-value">
          <rich-text-card
            :title="t('question_component_explanation_label')"
            :content="feedback"
            truncation-type="secondary"
          >
            <template #icon><svg-icon name="Feedback32" kind="indicative" /></template>
          </rich-text-card>
        </dd>
      </dl>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm";
import { isEmpty } from "@/utils/var.check";
import appConfigs from "@configs/app";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import AccordionWrapper from "@plugins/common/components/AccordionWrapper.vue";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";
import AttachmentPlaceholder from "@plugins/common/components/AttachmentPlaceholder.vue";
import RichTextCard from "@plugins/richtext/components/RichTextCard.vue";
import { QuestionModel } from "@modules/editor/types.ts";
import { type D3Api, useQuestionDnd } from "@modules/editor/composables/use-question-dnd.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";
import DragDropResponseOption from "@modules/attempt/components/question/DragDropResponseOption.vue";
import DragDropUnusedOption from "@modules/attempt/components/question/DragDropUnusedOption.vue";

defineOptions({
  name: "DragDropView",
});

// Props
interface Props {
  question: QuestionModel;
  highlights?: object | null;
}

const props = withDefaults(defineProps<Props>(), {
  highlights: null,
});

// Composables
const { t } = useI18n();
const { vm, vmExpose } = useVm();
const { canAccessAnnotations } = useAnnotationPermissions();
const { questionBodyAnchor, questionAnnotationResourceId } = useQuestionAnnotation();
const questionDnd = useQuestionDnd({
  d3: () => (vm as unknown as { $d3: D3Api }).$d3,
  el: () => vm.$el as Element | undefined,
  nextTick: () => nextTick(),
  question: () => props.question,
});
const { bgImageRatio, handleIndicatorClick, indicatorClasses, initBackgroundImage } = questionDnd;

// Template refs
const container = useTemplateRef<HTMLElement>("container");
const imageWrapper = useTemplateRef<SVGElement>("imageWrapper");

// Computed properties
const questionBody = computed(() => {
  return props.question.questionBody;
});
const feedback = computed(() => {
  return props.question.feedbackWrong;
});

const answerOptions = computed(() => {
  return props.question.questionOptionModels ? props.question.questionOptionModels : [];
});

const bgImageSrc = computed(() => {
  const isValidUuid =
    props.question &&
    !isEmpty(props.question.uuid) &&
    props.question.uuid !== "newQuestion" &&
    props.question.uuid !== "newQuizQuestion";

  return isValidUuid
    ? `${appConfigs.s3ResourceDomain}/question/${props.question.uuid}/${props.question.fileName}`
    : "";
});

const correctAnswerOptions = computed(() => {
  return answerOptions.value
    .filter((questionOption) => questionOption.answerOption)
    .map((questionOption) => {
      const questionOptionAnswer = questionOption.answer ? JSON.parse(questionOption.answer) : {};
      return {
        ...questionOptionAnswer,
        fileName: questionOption.fileName,
        isCorrect: true,
      };
    });
});

const questionAnswerUnusedOptions = computed(() => {
  return answerOptions.value
    .filter((questionOption) => !questionOption.answerOption)
    .map((questionOption) => {
      const questionOptionAnswer = questionOption.answer ? JSON.parse(questionOption.answer) : {};
      return {
        ...questionOptionAnswer,
        fileName: questionOption.fileName,
        src: `${appConfigs.s3ResourceDomain}/question/${props.question.uuid}/${questionOption.fileName}`,
      };
    });
});

const questionHints = computed(() => {
  return props.question.questionHintModels;
});

const hintId = computed(() => {
  return (hint: { id: number | null }, index: number) => {
    return hint.id ? hint.id.toString() : index.toString();
  };
});

const hintTitle = computed(() => {
  return (index: number) => {
    return `${t("question_component_label_hint")} ${index + 1}`;
  };
});

// Lifecycle hooks
onMounted(() => {
  const img = new Image();
  img.src = bgImageSrc.value;
  img.addEventListener(
    "load",
    () => {
      if (imageWrapper.value) {
        initBackgroundImage(img.width, img.height, imageWrapper.value);
      }
    },
    false,
  );
});

vmExpose(questionDnd);
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.drag-drop-view {
  @include media("mobile-landscape", "desktop") {
    .question-view {
      &.image-rescaled {
        .bg-image-view {
          min-height: $drag-drop-media-min-height;
        }
      }
    }
  }

  .feedback-divider {
    height: 1px;
    width: 100%;
    background-color: $ui-03;
  }

  .question-view {
    .bg-image-view {
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      text-align: center;
      max-width: 100%;
      position: relative;
      margin: auto;
      background-color: $ui-03;

      @include media("tablet-max") {
        min-height: calc((var(--vh, 1vh) * 40));
        max-height: calc((var(--vh, 1vh) * 50));
      }

      .image-wrapper {
        position: relative;
        display: inline-block;
        max-height: $drag-drop-background-max-height;
        touch-action: none;
        transform-origin: top left;
        overflow: visible;

        .standard-width & {
          @include media("desktop") {
            max-width: $drag-drop-background-width;
          }
        }

        .drop-zone,
        .drop-target {
          margin: 0;
        }

        .bg-image {
          width: 100%;
        }
      }

      ~ .attachment-placeholder {
        background-color: $ui-01;
      }

      > .indicator {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-color: rgba($inverse-02, 0.5);
        color: $text-04;
        z-index: z-map("drag-drop-indicator");

        @include media("desktop") {
          display: none;
        }

        .sls-icon {
          margin-right: $spacing-03;

          [fill] {
            fill: currentColor;

            &[fill="#DFE8F9"] {
              opacity: 0.15;
            }
          }
        }

        &.hidden {
          display: none;
        }
      }
    }
  }
}
</style>
